/**
 * Domain awareness contracts + extraction (WP2).
 *
 * `DomainSchema` is the editor's distilled, framework-agnostic view of the host
 * data instance: the user-facing types (sigs) with their atoms, and the
 * relations (fields) with their arity / type signature. The UI uses it to power
 * dropdowns and completions; `domain-validation.ts` uses it to warn about
 * spec items that reference names absent from the current instance.
 *
 * `extractDomainSchema(instance)` is the adapter from a live
 * `IInputDataInstance` (e.g. an `AlloyDataInstance`). It is best-effort and
 * MUST NEVER throw — a malformed or partial instance degrades to an emptier
 * schema rather than crashing the editor.
 *
 * This module is framework-agnostic — no React. The type shape here is the
 * binding contract from `docs/SPEC_EDITOR_REDESIGN.md`.
 */

import type {
  IInputDataInstance,
  IType,
  IRelation,
} from '../../data-instance/interfaces';

export interface DomainRelation {
  name: string;
  arity?: number;
  /** type signature when known, e.g. ['Node', 'Node'] */
  typeSignature?: readonly string[];
}

export interface DomainSchema {
  types: ReadonlyArray<{ name: string; atoms: readonly string[] }>;
  relations: readonly DomainRelation[];
}

/** A schema with no types and no relations — the safe degenerate result. */
const EMPTY_SCHEMA: DomainSchema = { types: [], relations: [] };

/**
 * Built-in/internal type names that are always part of the selector language
 * rather than user domain types. These are never surfaced as domain types
 * (they are offered via selector keyword completions instead), and selectors
 * referencing them are never flagged by domain validation.
 */
const SELECTOR_BUILTIN_TYPES: ReadonlySet<string> = new Set([
  'univ',
  'Int',
  'seq/Int',
  'iden',
  'none',
  'String',
]);

function isBuiltinType(type: IType): boolean {
  // The instance API marks Alloy built-ins (univ/Int/seq/Int) via `isBuiltin`.
  // We honour that flag AND the name-based fallback above, because a few hosts
  // (and the programmatic build path) may not set the flag on built-ins.
  if (type.isBuiltin === true) {
    return true;
  }
  return SELECTOR_BUILTIN_TYPES.has(type.id);
}

/**
 * Is every type in this relation's signature a built-in/internal type?
 *
 * Alloy emits an internal `no-field-guard` relation over `[univ, univ]`; such
 * all-built-in-signature relations are housekeeping, not user fields, so we
 * drop them. A real user field always touches at least one user sig.
 */
function isInternalRelation(signature: readonly string[]): boolean {
  if (signature.length === 0) {
    return false;
  }
  return signature.every((t) => SELECTOR_BUILTIN_TYPES.has(t));
}

function extractTypes(instance: IInputDataInstance): DomainSchema['types'] {
  let rawTypes: readonly IType[];
  try {
    rawTypes = instance.getTypes();
  } catch {
    return [];
  }
  if (!Array.isArray(rawTypes)) {
    return [];
  }

  const out: Array<{ name: string; atoms: string[] }> = [];
  const seen = new Set<string>();

  for (const type of rawTypes) {
    try {
      if (!type || typeof type.id !== 'string' || type.id.length === 0) {
        continue;
      }
      if (isBuiltinType(type)) {
        continue;
      }
      if (seen.has(type.id)) {
        continue;
      }
      seen.add(type.id);

      const atoms: string[] = [];
      const atomSeen = new Set<string>();
      if (Array.isArray(type.atoms)) {
        for (const atom of type.atoms) {
          // Prefer the human label, fall back to the id.
          const name =
            atom && typeof atom.label === 'string' && atom.label.length > 0
              ? atom.label
              : atom && typeof atom.id === 'string'
                ? atom.id
                : undefined;
          if (name !== undefined && !atomSeen.has(name)) {
            atomSeen.add(name);
            atoms.push(name);
          }
        }
      }
      out.push({ name: type.id, atoms });
    } catch {
      // Skip a single malformed type rather than failing the whole extraction.
      continue;
    }
  }
  return out;
}

function extractRelations(instance: IInputDataInstance): DomainRelation[] {
  let rawRelations: readonly IRelation[];
  try {
    rawRelations = instance.getRelations();
  } catch {
    return [];
  }
  if (!Array.isArray(rawRelations)) {
    return [];
  }

  const out: DomainRelation[] = [];
  const seen = new Set<string>();

  for (const relation of rawRelations) {
    try {
      // `name` is the bare field name used in selectors (the qualified
      // `Sig<:field` form lives in `id`, which selectors do not use).
      const name =
        relation && typeof relation.name === 'string' && relation.name.length > 0
          ? relation.name
          : undefined;
      if (name === undefined) {
        continue;
      }

      const signature = Array.isArray(relation.types)
        ? relation.types.filter((t: unknown): t is string => typeof t === 'string')
        : [];

      if (isInternalRelation(signature)) {
        continue;
      }

      // Dedupe on (name + signature): two distinct fields can share a bare name
      // (e.g. two `Next` fields from separate orderings) and both are useful,
      // but an exact (name, signature) duplicate is noise.
      const key = `${name} ${signature.join(' ')}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);

      const rel: DomainRelation = { name };
      if (signature.length > 0) {
        rel.arity = signature.length;
        rel.typeSignature = signature;
      }
      out.push(rel);
    } catch {
      continue;
    }
  }
  return out;
}

/**
 * Adapter from the live instance. Never throws; returns a best-effort schema.
 *
 * Policy (decisions of record, see also `docs/SPEC_EDITOR_REDESIGN.md` WP2):
 *  - Built-in types flagged by the instance (`isBuiltin`) — and the well-known
 *    selector built-ins univ/Int/seq/Int/iden/none/String — are EXCLUDED from
 *    `types`. They are reachable through selector keyword completions and are
 *    never flagged by domain validation, so dropping them keeps type dropdowns
 *    focused on user sigs.
 *  - Relations whose entire type signature is built-in (e.g. Alloy's internal
 *    `no-field-guard` over `[univ, univ]`) are EXCLUDED as housekeeping.
 *  - `DomainRelation.arity`/`typeSignature` are derived from `relation.types`
 *    (the type signature; length = arity) when present.
 *  - Types and relations are de-duplicated; per-item failures are swallowed.
 */
export function extractDomainSchema(
  instance: IInputDataInstance | null | undefined,
): DomainSchema {
  if (instance === null || instance === undefined) {
    return EMPTY_SCHEMA;
  }
  try {
    return {
      types: extractTypes(instance),
      relations: extractRelations(instance),
    };
  } catch {
    // Belt-and-braces: the per-section helpers already swallow their own
    // errors, but never let a surprise throw escape this adapter.
    return EMPTY_SCHEMA;
  }
}
